/**
 * TicketsAdvancedFilters - Filtros avançados chip-based (Linear/Attio style)
 *
 * Exibe chips para filtros ativos com popover para seleção.
 * Suporta multiselect (status, priority) e singleselect boolean (SLA).
 *
 * @example
 * ```tsx
 * <TicketsAdvancedFiltersComponent
 *   filters={filters}
 *   onFiltersChange={setFilters}
 * />
 * ```
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FilterChip } from '@/shared/components/ui/FilterChip';
import { FiltersButton } from '@/shared/components/ui';
import type { TicketsAdvancedFilters, TicketPriority } from '../types';
import { TICKET_PRIORITY, TICKET_STATUS_CATEGORIES } from '../types';
import type { TicketStatusCategory } from '../types';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons'
// ============================================================
// Types
// ============================================================

interface FilterOption {
  value: string;
  label: string;
  color?: string;
  icon?: IconName;
}

type FilterType = 'multiselect' | 'boolean';

interface FilterDefinition {
  key: string;
  label: string;
  type: FilterType;
  icon: IconName;
  options: FilterOption[];
}

// ============================================================
// Props
// ============================================================

export interface TicketsAdvancedFiltersProps {
  /** Filtros avançados atuais */
  filters: TicketsAdvancedFilters;
  /** Callback quando filtros mudam */
  onFiltersChange: (filters: TicketsAdvancedFilters) => void;
  /**
   * Modo de renderização (mockup helpdesk-tickets):
   * - 'toolbar': APENAS o <FiltersButton> shared (funil + badge roxo inline, F05) + popover
   * - 'chips': APENAS os chips `.chipf` ativos + "Limpar tudo"
   * - 'full' (default): comportamento legado (chips + "+ Filtro" + Limpar)
   */
  variant?: 'full' | 'toolbar' | 'chips';
}

// ============================================================
// Constants
// ============================================================

const CATEGORY_ICONS: Record<string, IconName> = {
  new: 'alert-circle',
  in_progress: 'clock',
  waiting: 'pause-circle',
  resolved: 'check-circle',
  closed: 'x-circle',
};

const STATUS_CATEGORY_OPTIONS: FilterOption[] = Object.entries(TICKET_STATUS_CATEGORIES).map(([key, config]) => ({
  value: key,
  label: config.label,
  color: config.color,
  icon: CATEGORY_ICONS[key],
}));

const PRIORITY_OPTIONS: FilterOption[] = [
  { value: 'low', label: TICKET_PRIORITY.low.label, color: TICKET_PRIORITY.low.color },
  { value: 'medium', label: TICKET_PRIORITY.medium.label, color: TICKET_PRIORITY.medium.color },
  { value: 'high', label: TICKET_PRIORITY.high.label, color: TICKET_PRIORITY.high.color },
  { value: 'urgent', label: TICKET_PRIORITY.urgent.label, color: TICKET_PRIORITY.urgent.color },
];

const SLA_OPTIONS: FilterOption[] = [
  { value: 'ok', label: 'Dentro do SLA', color: '#10B981' },
  { value: 'breached', label: 'SLA Violado', color: '#EF4444' },
];

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    key: 'statusCategory',
    label: 'Categoria',
    type: 'multiselect',
    icon: 'alert-circle',
    options: STATUS_CATEGORY_OPTIONS,
  },
  {
    key: 'priority',
    label: 'Prioridade',
    type: 'multiselect',
    icon: 'alert-circle',
    options: PRIORITY_OPTIONS,
  },
  {
    key: 'slaBreached',
    label: 'SLA',
    type: 'boolean',
    icon: 'shield',
    options: SLA_OPTIONS,
  },
];

// ============================================================
// Helper functions
// ============================================================

/** Get active filter values for a given key */
function getActiveValues(filters: TicketsAdvancedFilters, key: string): string[] {
  switch (key) {
    case 'statusCategory':
      return filters.statusCategory || [];
    case 'priority':
      return filters.priority || [];
    case 'slaBreached':
      if (filters.slaBreached === undefined) return [];
      return [filters.slaBreached ? 'breached' : 'ok'];
    default:
      return [];
  }
}

/** Get display label for active filter values */
function getChipValue(def: FilterDefinition, filters: TicketsAdvancedFilters): string {
  const values = getActiveValues(filters, def.key);
  if (values.length === 0) return '';

  const labels = values
    .map(v => def.options.find(o => o.value === v)?.label)
    .filter(Boolean);

  return labels.join(', ');
}

/** Check if a filter has active values */
function isFilterActive(filters: TicketsAdvancedFilters, key: string): boolean {
  return getActiveValues(filters, key).length > 0;
}

// ============================================================
// Component
// ============================================================

export const TicketsAdvancedFiltersComponent: React.FC<TicketsAdvancedFiltersProps> = ({
  filters,
  onFiltersChange,
  variant = 'full',
}) => {
  // ---- State ----
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const [popoverStep, setPopoverStep] = useState<'list' | 'values'>('list');
  const [selectedFilter, setSelectedFilter] = useState<FilterDefinition | null>(null);
  const [tempValues, setTempValues] = useState<string[]>([]);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // ---- Refs ----
  // HTMLElement: no variant 'toolbar' o trigger é o wrapper do <FiltersButton>
  // shared (span), no 'full' é o próprio botão "+ Filtro".
  const triggerRef = useRef<HTMLElement | null>(null);
  const chipRefs = useRef<Record<string, HTMLElement | null>>({});
  const popoverRef = useRef<HTMLDivElement>(null);

  // ---- Derived ----
  const activeFilters = FILTER_DEFINITIONS.filter(def => isFilterActive(filters, def.key));
  const hasActiveFilters = activeFilters.length > 0;

  // ---- Position helpers ----
  const updatePosition = useCallback((rect: DOMRect) => {
    const popoverWidth = 240;
    let left = rect.left;
    if (left + popoverWidth > window.innerWidth - 16) {
      left = window.innerWidth - popoverWidth - 16;
    }
    setPopoverPosition({
      top: rect.bottom + 6,
      left,
    });
  }, []);

  // ---- Handlers ----
  const openFilterList = () => {
    if (triggerRef.current) {
      updatePosition(triggerRef.current.getBoundingClientRect());
    }
    setPopoverStep('list');
    setSelectedFilter(null);
    setOpenPopover('add');
  };

  const openFilterValues = (def: FilterDefinition, fromChip?: boolean) => {
    const ref = fromChip ? chipRefs.current[def.key] : triggerRef.current;
    if (ref) {
      updatePosition(ref.getBoundingClientRect());
    }
    setSelectedFilter(def);
    setTempValues(getActiveValues(filters, def.key));
    setPopoverStep('values');
    setOpenPopover(def.key);
  };

  const selectFilterFromList = (def: FilterDefinition) => {
    openFilterValues(def, false);
  };

  const closePopover = () => {
    setOpenPopover(null);
    setPopoverStep('list');
    setSelectedFilter(null);
    setTempValues([]);
  };

  // ---- Apply / Remove ----
  const applyMultiselect = () => {
    if (!selectedFilter) return;
    const newFilters = { ...filters };

    switch (selectedFilter.key) {
      case 'statusCategory':
        newFilters.statusCategory = tempValues.length > 0 ? (tempValues as TicketStatusCategory[]) : undefined;
        break;
      case 'priority':
        newFilters.priority = tempValues.length > 0 ? (tempValues as TicketPriority[]) : undefined;
        break;
    }

    onFiltersChange(newFilters);
    closePopover();
  };

  const applyBoolean = (value: string) => {
    const newFilters = { ...filters };
    newFilters.slaBreached = value === 'breached' ? true : false;
    onFiltersChange(newFilters);
    closePopover();
  };

  const removeFilter = (key: string) => {
    const newFilters = { ...filters };
    switch (key) {
      case 'statusCategory':
        delete newFilters.statusCategory;
        break;
      case 'priority':
        delete newFilters.priority;
        break;
      case 'slaBreached':
        delete newFilters.slaBreached;
        break;
    }
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    onFiltersChange({});
    closePopover();
  };

  const toggleTempValue = (value: string) => {
    setTempValues(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  // ---- Close on outside click ----
  useEffect(() => {
    if (!openPopover) return;
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        closePopover();
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePopover();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [openPopover]);

  // ---- Render popover content ----
  const renderPopoverContent = () => {
    // Step 1: Filter list
    if (popoverStep === 'list') {
      return (
        <div className="py-1">
          {FILTER_DEFINITIONS.map(def => {
            const active = isFilterActive(filters, def.key);
            return (
              <button
                key={def.key}
                onClick={() => selectFilterFromList(def)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors"
                style={{ color: '#1e293b' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {def.icon && <Icon name={def.icon} className="w-3.5 h-3.5" style={{ color: '#64748b' }} />}
                <span className="flex-1">{def.label}</span>
                {active && (
                  <Icon name="check" className="w-3.5 h-3.5" style={{ color: '#8b5cf6' }} />
                )}
              </button>
            );
          })}
        </div>
      );
    }

    // Step 2: Value selector
    if (!selectedFilter) return null;

    // Boolean filter (singleselect)
    if (selectedFilter.type === 'boolean') {
      const currentValues = getActiveValues(filters, selectedFilter.key);
      return (
        <div className="py-1">
          <div
            className="px-3 py-2 text-xs font-semibold uppercase tracking-wider"
            style={{ color: '#64748b' }}
          >
            {selectedFilter.label}
          </div>
          {selectedFilter.options.map(option => {
            const isSelected = currentValues.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => applyBoolean(option.value)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors"
                style={{
                  color: '#1e293b',
                  backgroundColor: isSelected ? '#f1f5f9' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = isSelected ? '#f1f5f9' : 'transparent';
                }}
              >
                {option.color && (
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: option.color }}
                  />
                )}
                <span className="flex-1">{option.label}</span>
                {isSelected && (
                  <Icon name="check" className="w-3.5 h-3.5" style={{ color: '#8b5cf6' }} />
                )}
              </button>
            );
          })}
        </div>
      );
    }

    // Multiselect filter
    return (
      <div>
        <div
          className="px-3 py-2 text-xs font-semibold uppercase tracking-wider"
          style={{ color: '#64748b', borderBottom: '1px solid #e5e7eb' }}
        >
          {selectedFilter.label}
        </div>
        <div className="py-1 max-h-64 overflow-y-auto">
          {selectedFilter.options.map(option => {
            const isChecked = tempValues.includes(option.value);
            return (
              <button
                key={option.value}
                onClick={() => toggleTempValue(option.value)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors"
                style={{ color: '#1e293b' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {/* Checkbox */}
                <div
                  className="w-4 h-4 rounded border flex items-center justify-center flex-shrink-0"
                  style={{
                    borderColor: isChecked ? '#8b5cf6' : '#d1d5db',
                    backgroundColor: isChecked ? '#8b5cf6' : '#ffffff',
                  }}
                >
                  {isChecked && <Icon name="check" className="w-3 h-3" style={{ color: '#ffffff' }} />}
                </div>
                {/* Color dot or icon */}
                {option.icon ? (
                  <Icon
                    name={option.icon}
                    className="w-3.5 h-3.5 flex-shrink-0"
                    style={{ color: option.color || '#64748b' }}
                  />
                ) : option.color ? (
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: option.color }}
                  />
                ) : null}
                <span className="flex-1">{option.label}</span>
              </button>
            );
          })}
        </div>
        {/* Apply button */}
        <div
          className="px-3 py-2 flex justify-end"
          style={{ borderTop: '1px solid #e5e7eb' }}
        >
          <button
            onClick={applyMultiselect}
            className="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
            style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#7c3aed';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#8b5cf6';
            }}
          >
            Aplicar
          </button>
        </div>
      </div>
    );
  };

  // ---- Popover (compartilhado pelas 3 variants) ----
  const popover = openPopover && createPortal(
    <div
      ref={popoverRef}
      className="fixed rounded-lg shadow-lg z-[9999]"
      style={{
        top: popoverPosition.top,
        left: popoverPosition.left,
        width: 240,
        backgroundColor: '#ffffff',
        border: '1px solid #e5e7eb',
      }}
    >
      {renderPopoverContent()}
    </div>,
    document.body
  );

  // ---- Variant 'toolbar': <FiltersButton> canônico (F05 — funil + badge roxo inline) ----
  if (variant === 'toolbar') {
    return (
      <>
        <span
          ref={(el) => { triggerRef.current = el; }}
          className="inline-flex flex-none"
        >
          <FiltersButton
            onClick={openFilterList}
            count={activeFilters.length}
            active={openPopover !== null}
            data-testid="tickets-filters-button"
          />
        </span>
        {popover}
      </>
    );
  }

  // ---- Variant 'chips': `.chipf` lilás ("Label: **Valor**" + ×) + Limpar tudo ----
  if (variant === 'chips') {
    if (activeFilters.length === 0) return null;
    return (
      <>
        {activeFilters.map(def => {
          const chipValue = getChipValue(def, filters);
          return (
            <span
              key={def.key}
              ref={(el) => { chipRefs.current[def.key] = el; }}
              className="inline-flex items-center gap-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap"
              style={{
                height: 26,
                padding: '0 6px 0 10px',
                backgroundColor: 'var(--theme-primary-background)',
                color: 'var(--theme-primary)',
              }}
              onClick={() => openFilterValues(def, true)}
              data-testid={`tickets-filter-chip-${def.key}`}
            >
              {def.label}: <b className="font-bold">{chipValue}</b>
              <button
                className="w-4 h-4 rounded-full inline-flex items-center justify-center transition-colors hover:bg-black/10"
                onClick={(e) => { e.stopPropagation(); removeFilter(def.key); }}
                aria-label="remover filtro"
              >
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        <button
          onClick={clearAllFilters}
          className="text-xs font-semibold transition-opacity hover:opacity-80 flex-none"
          style={{ color: 'var(--theme-primary)' }}
          data-testid="tickets-filters-clear"
        >
          Limpar tudo
        </button>
        {popover}
      </>
    );
  }

  // ---- Main render (variant 'full' — legado) ----
  return (
    <div className="flex items-center gap-2 flex-wrap flex-1">
      {/* Active filter chips */}
      {activeFilters.map(def => {
        const chipValue = getChipValue(def, filters);
        return (
          <div
            key={def.key}
            ref={(el) => { chipRefs.current[def.key] = el; }}
          >
            <FilterChip
              label={def.label}
              value={chipValue}
              onClick={() => openFilterValues(def, true)}
              onRemove={() => removeFilter(def.key)}
            />
          </div>
        );
      })}

      {/* Add filter button */}
      <button
        ref={(el) => { triggerRef.current = el; }}
        onClick={openFilterList}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-150"
        style={{
          color: 'var(--theme-muted-text)',
          backgroundColor: 'transparent',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
          e.currentTarget.style.color = 'var(--theme-header-text)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = 'var(--theme-muted-text)';
        }}
      >
        <Icon name="plus" className="w-3.5 h-3.5" />
        Filtro
      </button>

      {/* Clear all */}
      {hasActiveFilters && (
        <button
          onClick={clearAllFilters}
          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all duration-150"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--theme-header-text)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--theme-muted-text)';
          }}
        >
          <Icon name="x" className="w-3 h-3" />
          Limpar
        </button>
      )}

      {popover}
    </div>
  );
};

TicketsAdvancedFiltersComponent.displayName = 'TicketsAdvancedFiltersComponent';
